import React, { useContext, useRef, useState, useCallback } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Badge
} from '@mui/material';
import {
  TextFields as TypographyIcon,
  Save as SaveIcon,
  FileUpload as ImportIcon,
  FileDownload as ExportIcon,
  PictureAsPdf as PdfIcon,
  Visibility as PreviewIcon,
  Edit as EditIcon,
  ViewColumn as LayoutIcon,
  Palette as DesignIcon,
  Close as CloseIcon,
  Brush as StylingIcon,
  DeleteOutline,
  Opacity as BackgroundIcon,
  FormatPaint as StyleIcon,
  AutoAwesomeMosaic as TemplateIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { FormContext } from '../../context/FormContext';
import { PaginationContext } from '../../context/PaginationContext';
import { useTypography } from '../../context/TypographyContext';
import { useLayout } from '../../context/LayoutContext';
import { useDesign } from '../../context/DesignContext';
import { useStyle } from '../../context/StyleContext';
import { useBackground } from '../../context/BackgroundContext';
import { useSpecialEffect } from '../../context/SpecialEffectContext';
import { useBarStyle } from '../../context/BarStyleContext';
import { enhancedExportJSON, enhancedImportJSON } from '../../services/ImportExportUtils';
import { allSections } from '../InputSection/InputSection';
import LayoutSelector from '../SettingsSection/LayoutSelector';
import DesignSelector from '../SettingsSection/DesignSelector';
import TypographySettings from '../SettingsSection/TypographySettings';
import StyleSelector from '../SettingsSection/StyleSelector';
import BackgroundSelector from '../SettingsSection/BackgroundSelector';
import TemplateSelector from '../SettingsSection/TemplateSelector';
import LanguageSwitcher from '../shared/LanguageSwitcher';
import { clearAllData } from "../../services/DataService";
import { tokens } from '../../theme/index';
import { useExport } from '../../context/ExportContext';
import { useLanguage } from '../../localization';
import PaymentModal from '../Payment/PaymentModal';
import stripePromise from '../../config/stripe';
import { useBackupReminder } from '../../hooks/useBackupReminder';


function MobileNavBar({
  showPreview,
  setShowPreview,
  openTypographySettings,
  previewRef,
  setSelectedSections,
  selectedSections,
  openDesignLayoutPicker,
  showSettingsInEditor,
  toggleEditorView,
}) {
  const theme = useTheme();
  const { formData, updateFormData } = useContext(FormContext);
  const { pages } = useContext(PaginationContext);
  const { typographySettings, updateTypographySettings } = useTypography();
  const { selectedLayout, setSelectedLayout } = useLayout();
  const {
    selectedDesign,
    setSelectedDesign,
    customColorScheme,
    setFullCustomColorScheme,
    currentColors
  } = useDesign();
  const { selectedStyleId, setSelectedStyleId } = useStyle();
  const { selectedBackgroundId, setSelectedBackgroundId } = useBackground();
  const { selectedSpecialEffectId, setSelectedSpecialEffectId } = useSpecialEffect();
  const { selectedBarStyleId, setSelectedBarStyleId } = useBarStyle();
  const fileInputRef = useRef(null);
  const { initiatePdfExport } = useExport();
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const { t } = useLanguage();

  // Use the backup reminder hook for mobile
  const {
    showReminder: showBackupBadge,
    handleUserInteraction,
    handleSaveAction,
  } = useBackupReminder({
    formData,
    isMobile: true,
  });

  // Validation function to check if PDF content exists
  const validatePdfContent = useCallback(() => {
    // Same validation logic as in ExportContext
    if (!pages || pages.length === 0) {
      console.warn("PDF Export validation failed: No pages calculated.");
      alert("Cannot export PDF: No content pages have been generated.");
      return false;
    }

    if (!formData) {
      console.warn("PDF Export validation failed: Form data is not available.");
      alert("Cannot export PDF: Resume data is missing.");
      return false;
    }

    return true;
  }, [pages, formData]);

  // State for the styling dropdown menu
  const [stylingAnchorEl, setStylingAnchorEl] = useState(null);
  const openStylingMenu = Boolean(stylingAnchorEl);

  // State for the recovery dropdown menu
  const [recoveryAnchorEl, setRecoveryAnchorEl] = useState(null);
  const openRecoveryMenu = Boolean(recoveryAnchorEl);

  // State for modals
  const [typographyDialogOpen, setTypographyDialogOpen] = useState(false);
  const [layoutDialogOpen, setLayoutDialogOpen] = useState(false);
  const [designDialogOpen, setDesignDialogOpen] = useState(false);
  const [styleDialogOpen, setStyleDialogOpen] = useState(false);
  const [backgroundDialogOpen, setBackgroundDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  // Handle styling menu
  const handleStylingClick = (event) => {
    setStylingAnchorEl(event.currentTarget);
  };

  const handleStylingClose = () => {
    setStylingAnchorEl(null);
  };

  // Handle recovery menu
  const handleRecoveryClick = (event) => {
    setRecoveryAnchorEl(event.currentTarget);
    handleUserInteraction(); // Hide backup badge when user interacts with save options
  };

  const handleRecoveryClose = () => {
    setRecoveryAnchorEl(null);
  };

  // Handle clearing data
  const handleClear = () => {
    clearAllData(updateFormData, setSelectedSections, {
      showConfirmation: true,
      onComplete: handleRecoveryClose
    });
  };

  // Updated export handler
  const handleExportJSON = () => {
    const exportFn = () => enhancedExportJSON(
      formData,
      selectedLayout,
      currentColors,
      selectedStyleId,
      selectedBackgroundId,
      typographySettings,
      selectedSpecialEffectId,
      selectedBarStyleId,
      selectedSections,
      handleRecoveryClose
    );
    
    handleSaveAction(exportFn);
  };

  // Updated import handler
  const handleImportJSON = (event) => {
    const file = event.target.files[0];
    enhancedImportJSON(
      file,
      setSelectedLayout,
      setFullCustomColorScheme,
      setSelectedDesign,
      setSelectedStyleId,
      setSelectedBackgroundId,
      updateTypographySettings,
      setSelectedSpecialEffectId,
      setSelectedBarStyleId,
      updateFormData,
      setSelectedSections,
      allSections,
      {
        onComplete: () => {
          handleRecoveryClose();
          event.target.value = null; // Reset file input
        }
      }
    );
  };

  // Open modal for typography settings
  const handleOpenTypography = () => {
    setTypographyDialogOpen(true);
    handleStylingClose();
  };

  // Open modal for layout selection
  const handleOpenLayout = () => {
    setLayoutDialogOpen(true);
    handleStylingClose();
  };

  // Open modal for design selection
  const handleOpenDesign = () => {
    setDesignDialogOpen(true);
    handleStylingClose();
  };

  // Handlers for new modals
  const handleOpenStyle = () => {
    setStyleDialogOpen(true);
    handleStylingClose();
  };

  const handleOpenBackground = () => {
    setBackgroundDialogOpen(true);
    handleStylingClose();
  };

  const handleOpenTemplate = () => {
    setTemplateDialogOpen(true);
    handleStylingClose();
  };

  // Handle successful payment - proceed with PDF export
  const handlePaymentSuccess = useCallback(async () => {
    setIsExportingPdf(true);
    try {
      await initiatePdfExport();
    } catch (error) {
      console.error("MobileNavBar PDF export error:", error);
      // Consider adding user feedback here
    } finally {
      setIsExportingPdf(false);
    }
  }, [initiatePdfExport]);

  // PDF Export Handler
  const handleExportPDF = useCallback(async () => {
    if (isExportingPdf) return;
    
    // Validate PDF content before proceeding with payment or export
    if (!validatePdfContent()) {
      return; // Stop here if validation fails
    }
    
    // If Stripe is not configured, proceed directly with PDF export
    if (!stripePromise) {
      console.warn('Stripe not configured - proceeding with direct PDF export');
      setIsExportingPdf(true);
      try {
        await initiatePdfExport();
      } catch (error) {
        console.error("MobileNavBar PDF export error:", error);
      } finally {
        setIsExportingPdf(false);
      }
      return;
    }
    
    // Only show payment modal if validation passed
    setShowPaymentModal(true);
  }, [isExportingPdf, initiatePdfExport, validatePdfContent]);

  // Handle payment modal close
  const handlePaymentClose = useCallback(() => {
    setShowPaymentModal(false);
  }, []);

  return (
    <Box sx={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: tokens.colors.alpha.black[800],
      borderTop: `1px solid ${theme.palette.divider}`,
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'center',
      padding: '8px 0',
      zIndex: 100,
      boxShadow: '0px -2px 4px rgba(0,0,0,0.05)'
    }}>

      {/* Styling Button */}
      <Tooltip title={t('mobile.stylingOptions')} placement="top">
        <IconButton
          onClick={handleStylingClick}
          sx={{
            backgroundColor: theme.palette.action.hover,
            width: 40,
            height: 40,
          }}
        >
          <StylingIcon />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={stylingAnchorEl}
        open={openStylingMenu}
        onClose={handleStylingClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <MenuItem onClick={handleOpenLayout}>
          <ListItemIcon>
            <LayoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('settings.layout')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenDesign}>
          <ListItemIcon>
            <DesignIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('settings.design')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenStyle}>
          <ListItemIcon>
            <StyleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('settings.style')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenBackground}>
          <ListItemIcon>
            <BackgroundIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('settings.background')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenTemplate}>
          <ListItemIcon>
            <TemplateIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('settings.template')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenTypography}>
          <ListItemIcon>
            <TypographyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('settings.typography')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Recovery Button with Badge */}
      <Tooltip title={t('mobile.importExport')} placement="top">
        <Badge
          color="primary"
          variant="dot"
          invisible={!showBackupBadge}
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: tokens.colors.warning,
              animation: showBackupBadge ? 'pulse 2s infinite' : 'none',
              '@keyframes pulse': {
                '0%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
                '50%': {
                  transform: 'scale(1.2)',
                  opacity: 0.7,
                },
                '100%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
              }
            }
          }}
        >
          <IconButton
            onClick={handleRecoveryClick}
            sx={{
              backgroundColor: tokens.colors.primary.light,
              color: '#ffffff',
              width: 40,
              height: 40,
            }}
          >
            <SaveIcon />
          </IconButton>
        </Badge>
      </Tooltip>

      <Menu
        anchorEl={recoveryAnchorEl}
        open={openRecoveryMenu}
        onClose={handleRecoveryClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <MenuItem onClick={handleExportJSON}>
          <ListItemIcon>
            <ExportIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('mobile.saveAsJson')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => fileInputRef.current.click()}>
          <ListItemIcon>
            <ImportIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('mobile.loadFromJson')}</ListItemText>
        </MenuItem>

        <input
          type="file"
          accept="application/json"
          hidden
          ref={fileInputRef}
          onChange={handleImportJSON}
        />
      </Menu>

      {/* Export PDF Button - Replaced ExportButton with IconButton */}
      <Tooltip title={stripePromise ? t('mobile.exportPdf') : 'Export PDF - Free'} placement="top">
        <IconButton
          onClick={handleExportPDF}
          disabled={isExportingPdf}
          sx={{
            backgroundColor: tokens.colors.primary.main,
            color: '#ffffff',
            width: 40,
            height: 40,
            '&:hover': {
              backgroundColor: tokens.colors.primary.dark,
            },
            '&.Mui-disabled': {
               backgroundColor: tokens.colors.neutral[300],
               color: tokens.colors.neutral[500],
            }
          }}
        >
          {isExportingPdf ? <CircularProgress size={24} color="inherit" /> : <PdfIcon />}
        </IconButton>
      </Tooltip>

      {/* Clear All Data Button */}
      <Tooltip title={t('mobile.clearAllData')} placement="top">
        <IconButton
          onClick={handleClear}
          sx={{
            backgroundColor: tokens.colors.error,
            color: '#ffffff',
            width: 40,
            height: 40,
          }}
        >
          <DeleteOutline />
        </IconButton>
      </Tooltip>

      {/* Toggle Preview/Edit */}
      <Tooltip title={showPreview ? t('mobile.editResume') : t('mobile.previewResume')} placement="top">
        <IconButton
          onClick={() => setShowPreview(!showPreview)}
          sx={{
            backgroundColor: tokens.colors.warning,
            color: '#ffffff',
            width: 40,
            height: 40,
          }}
        >
          {showPreview ? <EditIcon /> : <PreviewIcon />}
        </IconButton>
      </Tooltip>

      {/* Dialogs for styling options */}
      <Dialog
        open={layoutDialogOpen}
        onClose={() => setLayoutDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Choose Layout
          <IconButton onClick={() => setLayoutDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <LayoutSelector />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLayoutDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={designDialogOpen}
        onClose={() => setDesignDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Choose Design
          <IconButton onClick={() => setDesignDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DesignSelector />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDesignDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={typographyDialogOpen}
        onClose={() => setTypographyDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Typography Settings
          <IconButton onClick={() => setTypographyDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TypographySettings standalone={true} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTypographyDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={styleDialogOpen}
        onClose={() => setStyleDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Choose Style
          <IconButton onClick={() => setStyleDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <StyleSelector />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStyleDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={backgroundDialogOpen}
        onClose={() => setBackgroundDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Choose Background
          <IconButton onClick={() => setBackgroundDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <BackgroundSelector />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackgroundDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Apply Template
          <IconButton onClick={() => setTemplateDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TemplateSelector />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)} size="small">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Payment Modal */}
      <PaymentModal
        open={showPaymentModal}
        onClose={handlePaymentClose}
        onSuccess={handlePaymentSuccess}
        stripePromise={stripePromise}
        selectedSections={selectedSections}
      />

    </Box>
  );
}

export default MobileNavBar; 